import React, { useEffect, useRef } from 'react';
import { Button, Flex, Stack, Text, SimpleGrid, Select, MultiSelect } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { TemplateSectionCreationData } from 'src/company/templates/types';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import SplitMarkdownEditor from 'src/company/templates/components/template-creation-flow/steps/SplitMarkdownEditor';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';
import { PreserveAsIsToggle } from './PreserveAsIsToggle';
import { PageBreakToggle } from './PageBreakToggle';
import { SectionOptions } from 'src/company/templates/hooks/useSectionOptions';
import { TableOfContentsSettings } from 'src/company/templates/components/TableOfContentsSettings';

type Props = {
  section: TemplateSectionCreationData;
  onUpdateSection: (sectionId: string, updates: Partial<TemplateSectionCreationData>) => void;
  onToggleComplete: (sectionId: string) => void;
  isCompleted?: boolean;
  onSectionSplit: (sections: TemplateSectionCreationData[]) => void;
  placeholderImages?: MarkdownImage[];
  sectionOptions?: SectionOptions;
};

export function SectionContentEditorPane({
  section,
  onUpdateSection,
  onToggleComplete,
  onSectionSplit,
  placeholderImages = [],
  isCompleted = false,
  sectionOptions,
}: Props) {
  const [isInSplitMode, setIsInSplitMode] = React.useState(false);
  const splitSection = useRef([section]);
  const splitModeEnteredForSectionId = useRef<string | null>(null);

  useEffect(() => {
    if (
      splitModeEnteredForSectionId.current &&
      splitModeEnteredForSectionId.current !== section.id
    ) {
      setIsInSplitMode(false);
      splitModeEnteredForSectionId.current = null;
    }
    splitSection.current = [section];
  }, [section.id]);

  const handleTogglePreserve = (preserveAsIs: boolean) => {
    onUpdateSection(section.id, { preserveAsIs });
  };

  const handleTogglePageBreak = (addPageBreakAfterSection: boolean) => {
    onUpdateSection(section.id, { addPageBreakAfterSection });
  };

  const handleAgentChange = (value: string | null) => {
    onUpdateSection(section.id, { agent: value || undefined });
  };

  const handleDomainFilterChange = (value: string[]) => {
    onUpdateSection(section.id, { domainFilter: value.length > 0 ? value : undefined });
  };

  const isTableOfContents = section.sectionType === 'TABLE_OF_CONTENTS';

  const agentOptions = sectionOptions?.agents.map(agent => ({ value: agent, label: agent })) || [];
  const domainFilterOptions =
    sectionOptions?.domainFilters.map(filter => ({ value: filter, label: filter })) || [];

  const toggleSplitMode = () => {
    const entering = !isInSplitMode;
    setIsInSplitMode(entering);
    splitModeEnteredForSectionId.current = entering ? section.id : null;
  };

  return (
    <Stack gap="md" h="100%" style={{ minWidth: 0 }} flex={1}>
      <Flex align="center" justify="space-between" style={{ minWidth: 0 }}>
        <Text size="textLg" fw={FONT_WEIGHTS.semibold} c="gray.10" style={{ minWidth: 0 }} truncate>
          {section.sectionHeader}
        </Text>
        <Flex align="center" gap="xs" style={{ flexShrink: 0 }} direction={'row'}>
          <Button
            variant={'secondary'}
            size="xs"
            onClick={toggleSplitMode}
            leftSection={isCompleted ? <IconCheck size={14} /> : undefined}
          >
            {isInSplitMode ? 'Cancel split' : 'Split'}
          </Button>
          {isInSplitMode && (
            <Button
              variant={'primary'}
              size="xs"
              onClick={() => {
                onSectionSplit(splitSection.current);
                toggleSplitMode();
              }}
              leftSection={isCompleted ? <IconCheck size={14} /> : undefined}
            >
              Confirm split
            </Button>
          )}
          {!isInSplitMode && (
            <Button
              variant={isCompleted ? 'secondary' : 'primary'}
              size="xs"
              onClick={() => onToggleComplete(section.id)}
              leftSection={isCompleted ? <IconCheck size={14} /> : undefined}
            >
              {isCompleted ? 'Mark incomplete' : 'Mark complete'}
            </Button>
          )}
        </Flex>
      </Flex>

      {isInSplitMode && (
        <SplitMarkdownEditor
          section={section}
          onSectionSplit={sections => {
            splitSection.current = sections;
          }}
          placeholderImages={placeholderImages}
        />
      )}

      {!isInSplitMode && isTableOfContents && (
        <TableOfContentsSettings section={section} onUpdateSection={onUpdateSection} />
      )}

      {!isInSplitMode && !isTableOfContents && (
        <>
          <Flex gap="md" align="flex-end" wrap="wrap">
            <PreserveAsIsToggle checked={section.preserveAsIs} onChange={handleTogglePreserve} />
            <PageBreakToggle
              checked={section.addPageBreakAfterSection ?? false}
              onChange={handleTogglePageBreak}
            />
            {sectionOptions && (
              <>
                <Select
                  label="Agent"
                  placeholder="Select agent"
                  data={agentOptions}
                  value={section.agent || null}
                  onChange={handleAgentChange}
                  size="xs"
                  w={180}
                  clearable
                />
                <MultiSelect
                  label="Domain Filter"
                  placeholder="Select filters"
                  data={domainFilterOptions}
                  value={section.domainFilter || []}
                  onChange={handleDomainFilterChange}
                  size="xs"
                  w={250}
                  clearable
                />
              </>
            )}
          </Flex>
          <SimpleGrid cols={2} spacing="md" key={section.id} h="100%">
            <MarkdownEditor
              label="Template"
              defaultValue={section.templatedMarkdown}
              onChange={value => onUpdateSection(section.id, { templatedMarkdown: value })}
              h="100%"
              placeholderImages={placeholderImages}
            />
            <MarkdownEditor
              label="Populated"
              defaultValue={section.populatedMarkdown}
              onChange={value => onUpdateSection(section.id, { populatedMarkdown: value })}
              h="100%"
              placeholderImages={placeholderImages}
            />
          </SimpleGrid>
        </>
      )}
    </Stack>
  );
}
